import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { User } from '../types';

interface AuthContextType {
  user: SupabaseUser | null;
  profile: User | null;
  loading: boolean;
  isSuperAdmin: boolean;
  isClient: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Debug mode - activer pour voir les logs
const DEBUG_AUTH = true;
const logAuth = (...args: unknown[]) => {
  if (DEBUG_AUTH) console.log('[Auth]', ...args);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isMounted = useRef(true);
  const isFirstEvent = useRef(true);

  const loadProfile = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const userProfile = await authService.getUserProfile(userId);
      if (isMounted.current) {
        setProfile(userProfile);
      }
      return true;
    } catch (error) {
      console.error('Error loading profile:', error);
      if (isMounted.current) {
        setProfile(null);
      }
      return false;
    }
  }, []);

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    isFirstEvent.current = true;

    logAuth('AuthProvider mounted, listening for auth changes...');

    // Utiliser uniquement onAuthStateChange comme source de vérité
    // Le premier événement détermine l'état initial
    const unsubscribe = authService.onAuthStateChange(async (newUser) => {
      logAuth('onAuthStateChange:', newUser?.email ?? 'null', isFirstEvent.current ? '(first event)' : '');

      if (!isMounted.current) return;

      setUser(newUser);

      if (newUser) {
        await loadProfile(newUser.id);
      } else {
        setProfile(null);
      }

      // Après le premier événement, on arrête le loading
      if (isFirstEvent.current) {
        logAuth('First auth event received, setting loading to false');
        setLoading(false);
        isFirstEvent.current = false;
      }
    });

    // Timeout de sécurité si onAuthStateChange ne se déclenche jamais
    const timeoutId = setTimeout(() => {
      if (isFirstEvent.current && isMounted.current) {
        logAuth('Safety timeout - no auth event received, setting loading to false');
        setLoading(false);
        isFirstEvent.current = false;
      }
    }, 5000);

    return () => {
      logAuth('AuthProvider unmounting...');
      isMounted.current = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [loadProfile]);

  const login = async (email: string, password: string) => {
    const user = await authService.login(email, password);
    await loadProfile(user.id);
  };

  const logout = async () => {
    await authService.logout();
    setProfile(null);
  };

  const isSuperAdmin = profile?.role === 'super_admin';
  const isClient = profile?.role === 'client';

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      isSuperAdmin,
      isClient,
      login,
      logout,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
